import { IEnumDTO, IFilterDTO } from "../../../types/types";
import {
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TablePagination,
	TableRow,
	Theme,
	Typography,
	Button
} from "@mui/material";

import { DEFAULT_ROWS_PER_PAGE } from "../../../constants/CommonConstants";
import React, { useState, useEffect } from "react";
import SecuritiesTableFilter from "./SecuritiesTableFilter";
import SecuritiesTableHeader from "./SecuritiesTableHeader";
import SecuritiesTableRow from "./SecuritiesTableRow";
import SecuritiesCollapsibleTableRow from "./SecuritiesCollapsibleTableRow";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import DragProxy from "./DragProxy";
import { signal } from '@preact/signals-react';

const createLocalStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			width: "100%",
			marginBottom: '0rem',
		},
		paper: {
			width: "100%",
			marginBottom: theme.spacing(2),
			boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)"
		},
		title: {
			paddingTop: theme.spacing(1),
			paddingLeft: theme.spacing(2),
			fontWeight: "bold",
		},
		table: {
			minWidth: 800,
		},
		exportBtn: {
			float: "right",
			marginTop: theme.spacing(1),
			marginBottom: theme.spacing(1),
			marginRight: theme.spacing(2)
		}
	})
);

export interface ISecuritiesTableProps {  
	cssSelectorId?: string;  
	columns: IColumnHeader[];  
	subTableColumns?: IColumnHeader[];   
	rows: ISecuritiesTableFieldMap[] | null;  
	totalRowCount?: number;  
	sorter: ISorter;  
	paging?: string;	// if present, valid values are 'client' and 'server'   
	pageNumber?: number; // if paging == 'server' pass in the current page number (1-based)
	rowsPerServerPage?: number; // if paging == 'server' pass in the rows per page  
	rowsPerServerPageChanged?: (newRowsPerPage: number) => void;  
	title?: string;  
	showFilter?: boolean;  
	dataFetchFunc?: (filter: IFilterDTO[], pageNumber?: number, rowsPerPage?: number) => void; // pageNumber parameter is 1-based
	resetFilterFunc?: () => void;  
	maxTableHeight?: number;  
	collapsible?: boolean;  
	subTableTitle?: string;   
	dataExportFunc?: () => void;  
	enableExport?: boolean;  
	defaultFilter?: IFilterDTO[];  
	disableSorting?: boolean;  
	draggable?: boolean;
	dragProxyColumn?: string;
	handleReorderedRows?: (reorderedRows: ISecuritiesTableFieldMap[]) => void;
	handleSelectRows?: (selectedRows: ISecuritiesTableFieldMap[]) => void;
	selectedRowColor?: string;
}

const DEFAULT_MAX_TABLE_HEIGHT = 750;

export enum SelectionState {
	None = 1,
	Some,
	All,
}

export const rowSelectionState = signal(SelectionState.None);

export default function SecuritiesTable<T>({
	cssSelectorId,
	columns,
	subTableColumns,
	rows,
	totalRowCount,
	paging,
	pageNumber,
	rowsPerServerPage,
	rowsPerServerPageChanged,
	sorter,
	title,
	showFilter,
	dataFetchFunc,
	resetFilterFunc,
	maxTableHeight,
	collapsible,
	subTableTitle,
	dataExportFunc,
	enableExport = false,
	defaultFilter,
	disableSorting = false,
	draggable = false,
	dragProxyColumn = undefined,
	handleReorderedRows,
	handleSelectRows,
	selectedRowColor = 'cornsilk'
}: ISecuritiesTableProps) {
	const classes = createLocalStyles();
	const [order, setOrder] = React.useState<Order>(sorter.order);
	const [orderBy, setOrderBy] = React.useState<keyof ISecuritiesTableFieldMap>(sorter.property);
	const [page, setPage] = React.useState(pageNumber || 1); // 1-based page number
	const [rowsPerPage, setRowsPerPage] = React.useState(rowsPerServerPage || DEFAULT_ROWS_PER_PAGE);
	const activeSorter: ISorter = {
		property: orderBy,
		order: order,
	};

	const [disableSortBecauseOfDrag, setDisableSortBecauseOfDrag] =
		useState<boolean>(false);
	let proxyElement: HTMLElement | null = null;

	// Sync page state with pageNumber prop for server-side paging
	useEffect(() => {
		if (paging === 'server' && pageNumber !== undefined) {
			setPage(Math.max(1, pageNumber)); // pageNumber is 1-based
		}
	}, [pageNumber, paging]);

	// Sync rowsPerPage state with rowsPerServerPage prop
	useEffect(() => {
		if (paging === 'server' && rowsPerServerPage !== undefined) {
			setRowsPerPage(rowsPerServerPage);
		}
	}, [rowsPerServerPage, paging]);

	let localFilterCopy: IFilterDTO[];
	
	const updateLocalFilterCopy = (newFilter: IFilterDTO[]) => {
		localFilterCopy = newFilter;
	}

	const handleResetFilter = () => {
		if (defaultFilter) {
			localFilterCopy = defaultFilter;
		} else {
			localFilterCopy = [];
		}

		if (resetFilterFunc) {
			resetFilterFunc();
		}

		if (dataFetchFunc) {
			if (paging === 'server') {
				dataFetchFunc(localFilterCopy, 1, DEFAULT_ROWS_PER_PAGE); // 1-based page
			} else {
				dataFetchFunc(localFilterCopy)
			}

		}
	}

	const handleChangePage = (event: unknown, newPage: number) => {
		// Material-UI passes 0-based page, convert to 1-based
		const onePage = newPage + 1;
		setPage(onePage);

		if (paging === 'server') {
			if (paging === 'server' && dataFetchFunc !== undefined && localFilterCopy !== undefined) {
				dataFetchFunc(localFilterCopy, onePage, rowsPerPage);
			}
		}
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		const newRowsPerPage = parseInt(event.target.value, 10);
		setRowsPerPage(newRowsPerPage);

		if (rowsPerServerPageChanged) rowsPerServerPageChanged(newRowsPerPage);

		setPage(1); // Reset to page 1 (1-based)

		if (paging === 'server') {
			if (paging === 'server' && dataFetchFunc !== undefined && localFilterCopy !== undefined) {
				dataFetchFunc(localFilterCopy, 1, newRowsPerPage); // 1-based page
			}
		}
	};

	const handleSearchClick = (filter: IFilterDTO[]) => {
		if (dataFetchFunc !== undefined) {
			if (paging === 'server') {
				dataFetchFunc(filter, 1, rowsPerPage); // 1-based page
			} else {
				dataFetchFunc(filter);
			}
		}
	};

	const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof ISecuritiesTableFieldMap, filter?: IFilterDTO[]) => {
		if (disableSorting)
			return;

		setDisableSortBecauseOfDrag(false);

		const isAsc = orderBy === property && order === "asc";
		setOrder(isAsc ? "desc" : "asc");
		setOrderBy(property);

		sorter.property = property;
		sorter.order = isAsc ? "desc" : "asc";

		setPage(1); // Reset to page 1 (1-based)

		if (paging === 'server' && dataFetchFunc !== undefined && filter !== undefined) {
			dataFetchFunc(filter, 1, rowsPerPage); // 1-based page
		}
	};
	function genericSort(a: ISecuritiesTableFieldMap, b: ISecuritiesTableFieldMap, sorter: ISorter) {
		if (!sorter?.property) return 0;

		let objectA =
			a[sorter.property].sortValue === null || a[sorter.property].sortValue === undefined
				? a[sorter.property].value
				: a[sorter.property].sortValue;

		let objectB =
			b[sorter.property].sortValue === null || b[sorter.property].sortValue === undefined
				? b[sorter.property].value
				: b[sorter.property].sortValue;

		if (typeof objectA === "string" || objectA instanceof String) {
			objectA = objectA.toLowerCase();
		}

		if (typeof objectB === "string" || objectB instanceof String) {
			objectB = objectB.toLowerCase();
		}

		const result = () => {
			if (objectA === null) {
				return -1;
			}

			if (objectB === null) {
				return 1;
			}

			if (objectA > objectB) {
				return 1;
			}

			if (objectA < objectB) {
				return -1;
			}

			return 0;
		};

		return sorter.order !== "asc" ? result() * -1 : result();
	}

	const [selectedRowIds, setSelectedRowIds] = React.useState<Set<number>>(
		new Set()
	);

	const handleSelectAll = (selected: boolean) => {
		let selectedRows: ISecuritiesTableFieldMap[] = [];
		let workSet = new Set<number>();

		if (selected) {
			for (let i = 0; i < rows!.length; i++) {
				selectedRows.push(rows![i]);
				workSet.add(rows![i].selectId.value); 
			}

			rowSelectionState.value = SelectionState.All;
		} else {
			rowSelectionState.value = SelectionState.None;
		}

		setSelectedRowIds(workSet);
		handleSelectRows!(selectedRows);
	};


	const handleSelect = (id: number, isSelected: boolean) => {
		let workSet = new Set(selectedRowIds);

		if (isSelected) {
			workSet.add(id);
		} else {
			workSet.delete(id);
		}

		let selectedRows: ISecuritiesTableFieldMap[] = [];
		for (let i = 0; i < rows!.length; i++) {
			if (workSet.has(rows![i].selectId.value)) {
				selectedRows.push(rows![i]);
			}
		}

		if (workSet.size === 0) {
			rowSelectionState.value = SelectionState.None;
		} else if (workSet.size === rows?.length) {
			rowSelectionState.value = SelectionState.All;
		} else {
			rowSelectionState.value = SelectionState.Some;
		}

		setSelectedRowIds(workSet);
		handleSelectRows!(selectedRows);
	};

	const processRows = (rows: ISecuritiesTableFieldMap[]) => {
		if (paging === 'client') {
			if (!disableSortBecauseOfDrag) {
				rows.sort((a, b) => genericSort(a, b, activeSorter));
			}

			if (!draggable && !handleSelectRows) {
				// Use 1-based page for slicing: page 1 = rows 0-9, page 2 = rows 10-19, etc.
				const startIndex = (page - 1) * rowsPerPage;
				const endIndex = startIndex + rowsPerPage;
				rows = rows.slice(startIndex, endIndex);
			}

			return rows.map((row: any, index) => {
					return (
						<SecuritiesTableRow
							draggable={draggable}
							dragProxyColumn={dragProxyColumn}
							columns={columns}
							row={row}
							key={index}
							index={index}
							handleSelect={handleSelectRows ? handleSelect : undefined}
							selected={row.selectId && selectedRowIds.has(row.selectId.value) ? true : undefined}
							selectedRowColor={selectedRowColor }

						/>
					);
				});
		}

		return rows
			.sort((a, b) => genericSort(a, b, activeSorter))
			.map((row: any, index) => {
				if (collapsible && subTableColumns) {
					return <SecuritiesCollapsibleTableRow 
					columns={ columns } 
					subTableColumns={ subTableColumns }
					row={ row } 
					index={ index } 
					key={ index} 
					collapse={ row.collapsible.value }
					subTableTitle={ subTableTitle }
					/>
				}

				return <SecuritiesTableRow
					columns={columns}
					row={row}
					index={index}
					key={index}
					handleSelect={handleSelectRows ? handleSelect : undefined}
					selected={row.selectId && selectedRowIds.has(row.selectId.value) ? true : undefined}
					selectedRowColor={selectedRowColor}
				/>;

			});
	};

	const handleExport = (e: React.MouseEvent) => {
		e.preventDefault();

		if (dataExportFunc) {
			dataExportFunc();
		}

		return false;
	}

	//stuff for drag
	let dragIndex: number = -1;
	let prevDragOverElement: HTMLElement | null;

	useEffect(() => {
		if (!rows || rows.length === 0) {
			return;
		}

		if (draggable) {
			hookupPlumbing();
		}
	}, [rows]);

	const dragStartHandler = (event: DragEvent) => {
		if (!event.target) {
			return;
		}

		let target: HTMLElement = event.target as HTMLElement;

		let id = target.id;
		
		let rowElement = target.closest(`.${draggableRowRootClass}`) as HTMLElement;
		event.dataTransfer?.setData('text/plain', rowElement.id);

		let idx: number = parseInt(id.substring(draggableHandleIdentifier.length), 10); //TODO: make this better!
		dragIndex = idx; //this exists outside this function so we can refer to it later

		proxyElement = document.getElementById('proxy') as HTMLElement;

		let contentElement = document.getElementById(`proxy-${idx}-${dragProxyColumn}`) as HTMLElement;

		if (proxyElement) {

			if (contentElement) {
				proxyElement.textContent = contentElement.textContent;
			}
			else {
				proxyElement.textContent = 'Dragging...';
			}

			event.dataTransfer?.setDragImage(proxyElement, 0, 0); // Use the proxy as the drag image
		}


		//set up drop. pass the id of the row that was selectect, so
		// we exclude it from the drop targets
		let dropTargets = getDropTargets(idx);

		if (!dropTargets) {
			return;
		}

		dropTargets.forEach((item: any) => {
			item.addEventListener('drop', dropHandler);
			item.addEventListener('dragover', dragOverHandler);
			item.addEventListener('dragleave', dragLeaveHandler);
		});
	};

	const dragEndHandler = (event: DragEvent) => {
		if (!event.target) {
			return;
		}

		let target: HTMLElement = event.target as HTMLElement;
		let id = target.id;

		let idx: number = parseInt(target.id.substring(draggableHandleIdentifier.length), 10);
		
		// Clean up the proxy element
		if (proxyElement) {
			proxyElement = null;
		}

		//remove drop
		let dropTargets = getDropTargets(idx);

		if (!dropTargets) {
			return;
		}

		dropTargets.forEach((item: any) => {
			item.removeEventListener('drop', dropHandler);
			item.removeEventListener('dragover', dragOverHandler);
			item.removeEventListener('dragleave', dragLeaveHandler);
			item.style.border = '';
		});
	};

	const dragOverHandler = (event: DragEvent) => {
		event.preventDefault();

		let target: HTMLElement = event.target as HTMLElement;

		if (!target) {
			return;
		}

		let overId = target.id;
		let dragOverElement: HTMLElement;

		//if we drop on the drag handler, find the id of the row
		if (overId.indexOf(draggableRowIdentifier) === 0) {
			dragOverElement = target;
			overId = dragOverElement.id;
		} else {
			// they're over the drag handle, not the div
			dragOverElement = target.closest(`.${draggableRowRootClass}`) as HTMLElement;
			overId = dragOverElement.id;
		}

		let overIndex: number = parseInt(overId.substring(draggableRowIdentifier.length ), 10);
		const draggingBorderStyle = '3px solid blue';
		
		dragOverElement.style.height = '40px';
		dragOverElement.style.width = '270px';
		dragOverElement.style.padding = '10px';
		dragOverElement.style.border = '';

		if (dragIndex < overIndex) {
			dragOverElement.style.borderTop = '';
			dragOverElement.style.borderBottom = draggingBorderStyle;
		} else if (dragIndex > overIndex) {
			dragOverElement.style.borderTop = draggingBorderStyle;
			dragOverElement.style.borderBottom = '';
		}

		if (prevDragOverElement && prevDragOverElement != dragOverElement) {
			prevDragOverElement.style.border = '';
		}

		prevDragOverElement = dragOverElement;
	};

	const dragLeaveHandler = (event: DragEvent) => {
		let target: HTMLElement = event.target as HTMLElement;

		if (target.id.indexOf(draggableRowIdentifier) == -1) {
			let el: HTMLElement = target.closest(`.${draggableRowRootClass}`) as HTMLElement;
			el.style.border = '';
			return;
		}

		target.style.border = '';
	};

	const dropHandler = (event: DragEvent) => {
		event.preventDefault();

		setDisableSortBecauseOfDrag(true);

		let target: HTMLElement = event.target as HTMLElement;

		if (!target) {
			return;
		}

		let dropId = target.id;

		//if we drop on the draghandler, find the id of the row
		if (dropId.indexOf(draggableRowIdentifier) == -1) {
			let el: HTMLElement = target.closest(`.${draggableRowRootClass}`) as HTMLElement;
			dropId = el.id;
		}

		//let dropIdx: number = parseInt(dropId.substring(4), 10);
		let dropIdx: number = parseInt(dropId.substring(draggableRowIdentifier.length), 10);
		let dragId = event.dataTransfer?.getData('text/plain');

		if (!dragId) {
			return;
		}

		let dragIdx = parseInt(dragId.substring(draggableRowIdentifier.length), 10);

		if (!rows || rows.length === 0) {
			return;
		}

		let reorderedRows = [...rows];

		let removed = reorderedRows.splice(dragIdx, 1);
		reorderedRows.splice(dropIdx, 0, removed[0]);

		if (handleReorderedRows) {
			handleReorderedRows(reorderedRows);
		}
	};

	const hookupPlumbing = () => {
		if (!rows || rows.length === 0) {
			return;
		}

	 for (var i = 0; i < rows.length; i++) {
			var dragHandle: HTMLElement | null = document.getElementById(`drag-${i}`);

			if (dragHandle) {
				dragHandle.addEventListener('dragstart', dragStartHandler);
				dragHandle.addEventListener('dragend', dragEndHandler);
			}
		}
	};

	const getDropTargets = (selectedIndex: number) => {
		if (!rows || rows.length === 0) {
			return;
		}

		let dropTargets = [];

		for (var i = 0; i < rows.length; i++) {
			if (i === selectedIndex) {
				continue;
			}

			let element = document.getElementById(`row-${i}`);

			if (element) {
				dropTargets.push(element);
			}
		}

		return dropTargets;
	};

	//end of stuff for drag

	return (
		<div className={classes.root}>
			<Paper className={classes.paper}>
				{title ? <Typography className={classes.title}>{title}</Typography> : null}

				{enableExport &&
					<Button variant="contained" className={classes.exportBtn} color={'primary'} onMouseDown={(e) => handleExport(e)} style={{zIndex: "999"}} >Export</Button>
				}

				<TableContainer sx={{ maxHeight: maxTableHeight || DEFAULT_MAX_TABLE_HEIGHT }}>
					<Table 
						id={cssSelectorId ? cssSelectorId : "securitiesTable"}
						className={classes.table}
						aria-labelledby="tableTitle"
						aria-label="enhanced table"
					>
						{(showFilter && !draggable) ? (
							<SecuritiesTableFilter
								filterColumns={columns}
								orderBy={orderBy}
								order={order}
								onRequestSort={handleRequestSort}
								fetchDataFunc={handleSearchClick}
								resetFilterFunc={handleResetFilter}
								updateParentFilterCopy={updateLocalFilterCopy}
								defaultFilter={defaultFilter}
								isSelectable={handleSelectRows ? true : false}
								handleSelectAll={handleSelectAll}
							/>
						) : (
								<SecuritiesTableHeader
									draggable={draggable}
									columns={columns}
									orderBy={orderBy}
									order={order}
									onRequestSort={handleRequestSort}
									isSelectable={handleSelectRows ? true : false}
									handleSelectAll={handleSelectAll}
								/>
						)}

						<TableBody>
							{!rows || rows.length === 0 ? (
								<TableRow>
									<TableCell colSpan={columns.length} style={{ "textAlign": "center" }}>
										{rows && <Typography id="noResults" variant='h6'>The search returned no results.</Typography>}
									</TableCell>
								</TableRow>
							) : (
								processRows(rows)
							)}
						</TableBody>
					</Table>
				</TableContainer>

				{(paging === 'server') &&  (
					<TablePagination
						id="securitiesTablePagination"
						rowsPerPageOptions={[5, 10, 25]}
						component="div"
						count={-1}	// -1 enables server side paging
						rowsPerPage={rowsPerPage}
						labelDisplayedRows={({ page: muiPage }) => {
							// Material-UI passes 0-based page, our state uses 1-based
							// Prevent display of invalid page ranges
							if ((rows?.length || 0) === 0 || page < 1) {
								return '';
							}

							// Use our 1-based page state for calculations
							let lowRecordNum = (page - 1) * rowsPerPage + 1;
							let highRecordNum = (page - 1) * rowsPerPage + Math.min(rowsPerPage, rows?.length || 0);

							// Only display if we have valid data
							if (highRecordNum < lowRecordNum) {
								return '';
							}

							return `${lowRecordNum} - ${highRecordNum} of ${totalRowCount || 0}`;
							}}
						nextIconButtonProps={((rows?.length || 0) < rowsPerPage) || ((page - 1) * rowsPerPage + (rows?.length || 0) === totalRowCount)
							? {disabled: true} : undefined }
						page={page - 1}
						onPageChange={handleChangePage}
						onRowsPerPageChange={handleChangeRowsPerPage}
					/>
				)}

				{(paging === 'client' && !draggable && !handleSelectRows) && (
					<TablePagination
						rowsPerPageOptions={[5, 10, 25]}
						component="div"
						count={(rows?.length || 0)}
						rowsPerPage={rowsPerPage}
						page={page - 1}
						onPageChange={handleChangePage}
						onRowsPerPageChange={handleChangeRowsPerPage}
					/>
				)}
				<DragProxy/>

			</Paper>
		</div>
	);
}

export type SecuritiesTableFieldType =
	| "string"
	| "number"
	| "datetime"
	| "date"
	| "selectOne"
	| "selectMany"
	| "link"
	| "action"
	| "boolean"
	| "array"
	| "selectManyString";

export type Order = "asc" | "desc";

export enum TableFieldType {
	link,
	button,
	iconButton,
	iconLink,
	date,
}

export interface IColumnHeader {
	field: keyof ISecuritiesTableFieldMap;
	label: string;
	parameterName?: string; // the parameter name in the stored proc that accesses the data
	type?: string; // the data type
	enumValues?: IEnumDTO[]; // for type=enum, this contains the values to be displayed in the select box
	value?: string; // the current filter value
	allowFilter?: boolean;
	hideHeader?: boolean;
	minWidth?: string;
	default?: string;
	style?: any;
	hideSortIcon?: boolean;
}

export interface ISorter {
	property: keyof ISecuritiesTableFieldMap;
	order: Order;
}

export interface ISecuritiesTableField {
	type: SecuritiesTableFieldType;
	value: any;
	sortValue?: string | number | Date | boolean;
}

export interface ISecuritiesTableFieldMap {
	[name: string]: ISecuritiesTableField;
}

export const draggableRowRootClass:string = 'js-row-root';
export const draggableRowIdentifier = 'row-';

export const draggableHandleRootClass: string = 'js-drag-handle';
export const draggableHandleIdentifier = 'drag-';

